import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Play, ArrowRight } from "lucide-react";
import VideoRow from "../components/VideoRow";
import ClientMarquee from "../components/ClientMarquee";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";

// Build VideoObject list for every video shown on the home page. This unlocks
// "video carousel" rich results in Google search and Google Discover.
const homeVideoListJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: Object.values(HOME_ROWS)
    .flatMap((row) => row.videos)
    .filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i)
    .map((v, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "VideoObject",
        name: `${v.client} — ${v.title}`,
        description: `${v.title} video produced by Yellow Video Production Liverpool for ${v.client}.`,
        thumbnailUrl: THUMBS[v.id] || `https://vumbnail.com/${v.id}.jpg`,
        uploadDate: "2024-01-01",
        contentUrl: `https://vimeo.com/${v.id}${v.h ? `/${v.h}` : ""}`,
        embedUrl: `https://player.vimeo.com/video/${v.id}${v.h ? `?h=${v.h}` : ""}`,
        publisher: { "@id": "https://yellowvideoproduction.com/#organization" },
        inLanguage: "en-GB",
      },
    })),
};
import { HERO_REEL_VIMEO_ID, HERO_REEL_YOUTUBE_ID, HERO_POSTER, HOME_ROWS, THUMBS } from "../data/content";

const SERVICE_TILES = [
  {
    label: "Video Production",
    sub: "Filming, editing & motion graphics",
    href: "/video-production-in-liverpool",
    img: "/services/video-production-liverpool-camera-operator.jpg",
  },
  {
    label: "Photography",
    sub: "Location & studio shoots, UK wide",
    href: "/photography-liverpool",
    img: "/services/photography-liverpool-photographer.jpg",
  },
  {
    label: "Animation",
    sub: "2D, 3D, motion graphics & CGI",
    href: "/animation-liverpool",
    img: "/services/animation-liverpool-motion-graphics-studio.jpg",
  },
  {
    label: "Drone",
    sub: "CAA-licensed aerial filming",
    href: "/drone-filming-aerial-photography-liverpool",
    img: "/thumbs/665944961.jpg",
  },
  {
    label: "White Label",
    sub: "Invisible production for agencies",
    href: "/white-label",
    img: "/services/white-label-video-production-liverpool-edit-suite.jpg",
  },
];

export default function Home() {
  const iframeRef = useRef(null);
  // YouTube's iframe uses a different postMessage API to Vimeo, so we skip the
  // "wait for playing event" gate and reveal the iframe immediately when using
  // YouTube. Muted autoplay is reliable across modern browsers.
  const [videoOk, setVideoOk] = useState(Boolean(HERO_REEL_YOUTUBE_ID));

  // Listen for Vimeo player postMessage events. Iframe is initially hidden; only
  // revealed once the player actually emits a "playing" event. If the iframe shows
  // a Cloudflare challenge (or any other non-Vimeo content), no event fires and
  // the poster image continues to be the visible hero.
  useEffect(() => {
    const onMsg = (e) => {
      if (typeof e.data !== "string" || !e.data) return;
      try {
        const data = JSON.parse(e.data);
        if (data.event === "ready") {
          // Subscribe to events we care about.
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: "addEventListener", value: "playing" }),
            "*"
          );
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: "addEventListener", value: "play" }),
            "*"
          );
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: "addEventListener", value: "error" }),
            "*"
          );
        }
        if (data.event === "playing" || data.event === "play") setVideoOk(true);
        if (data.event === "error") setVideoOk(false);
      } catch (_) {}
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // Kick off the Vimeo handshake on iframe load.
  const onIframeLoad = () => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ method: "ping" }),
        "*"
      );
    } catch (_) {}
  };

  return (
    <main data-testid="home-page">
      <SEO
        title="YELLOW Video | Video Production Liverpool"
        description="Award-winning video production in Liverpool. 1,000+ films for global brands, charities, universities & SMEs. Fixed-price quote in 4 hours. FREE stills photography included. UK wide."
        path="/"
        keywords="video production liverpool, liverpool video production, video production companies liverpool, video production company liverpool, videographers liverpool, video production crew liverpool, corporate video liverpool, video production agency liverpool"
        jsonLd={homeVideoListJsonLd}
      />
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] overflow-hidden" data-testid="home-hero">
        {/* Vimeo background reel (Pro account → supports background mode).
            Poster image sits underneath so the hero always has imagery, even before the
            video loads or in environments where the iframe is blocked. */}
        <div className="absolute inset-0 yv-hero-pan">
          <img
            src={HERO_POSTER}
            alt="Yellow Showreel"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="yv-hero-vid" style={{ opacity: videoOk ? 1 : 0, transition: "opacity 900ms ease" }}>
          <iframe
            ref={iframeRef}
            onLoad={onIframeLoad}
            src={
              HERO_REEL_YOUTUBE_ID
                ? `https://www.youtube.com/embed/${HERO_REEL_YOUTUBE_ID}?autoplay=1&mute=1&loop=1&playlist=${HERO_REEL_YOUTUBE_ID}&controls=0&modestbranding=1&playsinline=1&rel=0&showinfo=0`
                : `https://player.vimeo.com/video/${HERO_REEL_VIMEO_ID}?background=1&autoplay=1&loop=1&muted=1&autopause=0&controls=0&dnt=1&api=1`
            }
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Yellow Showreel"
          />
        </div>
        <div className="yv-hero-fade" />

        <div className="relative z-10 h-full flex flex-col justify-end pb-24 yv-container">
          <Reveal>
            <p className="yv-eyebrow mb-4">Video Production · UK Wide · Since 2012</p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="yv-h1 max-w-4xl">
              From script to <span style={{ color: "var(--yellow)" }}>screen</span>.<br />
              Without the headache.
            </h1>
          </Reveal>
          <Reveal delay={180}>
            <p className="text-[var(--yellow)] font-bold tracking-wide mt-3 text-sm md:text-base uppercase">
              Liverpool's award-winning video production company
            </p>
          </Reveal>
          <Reveal delay={220}>
            <p className="text-white/80 max-w-2xl mt-5 text-base md:text-lg leading-relaxed">
              We're Yellow, a <strong className="text-white">video production company in Liverpool</strong> that has produced
              <strong className="text-white"> 1,000+ films </strong>
              for global brands, government agencies, universities and ambitious SMEs.
              Planning, filming, editing and free stills photography in one tidy package, based in Merseyside but covering the entire UK.
            </p>
          </Reveal>
          <Reveal delay={320}>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/showreel" className="yv-btn yv-btn-primary" data-testid="hero-watch-reel">
                <Play size={16} fill="#0a0a0a" /> Watch the reel
              </Link>
              <Link to="/contact" className="yv-btn yv-btn-secondary" data-testid="hero-get-quote">
                Get a quote in 60 min <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>

          <div className="absolute right-6 bottom-24 hidden xl:block text-right">
            <p className="yv-eyebrow text-white/40 mb-1">Now playing</p>
            <p className="text-white font-bold">Yellow Showreel</p>
            <p className="text-white/50 text-sm">22 selected projects</p>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-white/40 text-xs gap-2 z-10">
          <span>Scroll</span>
          <div className="w-px h-10 bg-white/30 animate-pulse" />
        </div>
      </section>

      {/* CLIENTS */}
      <ClientMarquee />

      {/* SERVICES grid (Netflix categories) */}
      <section className="yv-container py-20">
        <Reveal>
          <p className="yv-eyebrow">What we do</p>
          <h2 className="yv-h2 text-white mt-2 max-w-3xl">
            Liverpool's full-service video production crew, one team, every format.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
          {SERVICE_TILES.map((s, i) => (
            <Reveal key={s.href} delay={i * 80}>
              <Link to={s.href} className="yv-service-tile block" data-testid={`service-tile-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                <img src={s.img} alt={s.label} loading="lazy" />
                <div className="yv-tile-meta">
                  <p className="text-white font-bold text-lg leading-tight">{s.label}</p>
                  <p className="text-white/60 text-xs mt-1">{s.sub}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured rows */}
      <section className="yv-container">
        <VideoRow title={HOME_ROWS.recent.title} eyebrow={HOME_ROWS.recent.eyebrow} videos={HOME_ROWS.recent.videos} testId="row-recent" />
        <VideoRow title={HOME_ROWS.brand.title} eyebrow={HOME_ROWS.brand.eyebrow} videos={HOME_ROWS.brand.videos} testId="row-brand" />
        <VideoRow title={HOME_ROWS.sme.title} eyebrow={HOME_ROWS.sme.eyebrow} videos={HOME_ROWS.sme.videos} testId="row-sme" />
        <VideoRow title={HOME_ROWS.internal.title} eyebrow={HOME_ROWS.internal.eyebrow} videos={HOME_ROWS.internal.videos} testId="row-internal" />
      </section>

      {/* Manifesto strip */}
      <section className="relative bg-[#0c0c0c] border-y border-white/5 py-20" data-testid="home-manifesto">
        <div className="yv-container grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="yv-eyebrow mb-3">Why Yellow</p>
            <h2 className="yv-h2 text-white mb-6">
              Video production<br />that feels like cinema.
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              We take care of the entire video production process so you don't have to. From concept and planning through filming, editing and final delivery, Yellow's Liverpool-based crew creates high-quality video content that showcases your brand at its best.
            </p>
            <p className="text-white/70 leading-relaxed">
              Our video crew covers the entire UK. Clients have ranged from Southampton to Aberdeen, wherever you're based, we can help.
            </p>
            <div className="flex gap-3 mt-8">
              <Link to="/contact" className="yv-btn yv-btn-primary">Start a project</Link>
              <Link to="/video-production-prices" className="yv-btn yv-btn-secondary">See prices</Link>
            </div>
          </Reveal>

          <Reveal delay={150} className="grid grid-cols-2 gap-4">
            <Stat n="1000+" l="Films produced" />
            <Stat n="2 wks" l="Average turnaround" />
            <Stat n="UK Wide" l="From Southampton to Aberdeen" />
            <Stat n="FREE" l="Photography with every video" />
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border border-white/10 rounded-md p-6 bg-white/[0.02] hover:border-[var(--yellow)] transition">
      <p className="text-3xl md:text-4xl font-black text-[var(--yellow)]">{n}</p>
      <p className="text-white/60 text-sm mt-2">{l}</p>
    </div>
  );
}
